/**
 * components/switch.tsx — the design's `.t-switch`.
 * 32×18 track, 14px knob, success when on. Optional label (leading or trailing).
 */
import React from 'react';
import { Pressable, View, StyleProp, ViewStyle } from 'react-native';
import { LightColors as C, FontSize as FS } from '../theme/theme';
import { toneColors } from '../theme/tones';
import { Txt } from './ui';

export interface SwitchProps {
  value: boolean;
  onValueChange: (next: boolean) => void;
  label?: string;
  /** Label side. 'right' (default) or 'left' (used in Settings rows). */
  labelSide?: 'left' | 'right';
  disabled?: boolean;
  /** Stretch label to fill, pushing the track to the edge (Settings rows). */
  spread?: boolean;
  /** 'danger' tints the OFF track red so an off toggle is easy to spot (e.g. the
   *  re-activate toggle on rejected proposals). */
  tone?: 'default' | 'danger';
  style?: StyleProp<ViewStyle>;
}

const Track: React.FC<{ on: boolean; tone?: 'default' | 'danger' }> = ({ on, tone }) => {
  const dc = toneColors('danger');
  // 'danger' → a SOLID red off-track so it's unmistakable (not a faint tint).
  const offBg = tone === 'danger' ? dc.fg : C.paper3;
  const offBorder = tone === 'danger' ? dc.fg : C.lineStrong;
  return (
  <View
    style={{
      width: 32,
      height: 18,
      borderRadius: 999,
      borderWidth: 1,
      backgroundColor: on ? C.success : offBg,
      borderColor: on ? C.success : offBorder,
      justifyContent: 'center',
    }}
  >
    <View
      style={{
        position: 'absolute',
        top: 1,
        left: 1,
        width: 14,
        height: 14,
        borderRadius: 7,
        backgroundColor: C.card,
        transform: [{ translateX: on ? 14 : 0 }],
        ...(typeof window !== 'undefined'
          ? { boxShadow: '0 1px 2px rgba(16,24,40,0.18)' }
          : {
              shadowColor: '#101828',
              shadowOpacity: 0.18,
              shadowRadius: 2,
              shadowOffset: { width: 0, height: 1 },
              elevation: 1,
            }),
      }}
    />
  </View>
  );
};

const Switch: React.FC<SwitchProps> = ({
  value,
  onValueChange,
  label,
  labelSide = 'right',
  disabled = false,
  spread = false,
  tone = 'default',
  style,
}) => {
  const labelNode = label ? (
    <Txt size={FS.md} color={C.ink2} style={spread ? { flex: 1 } : undefined}>
      {label}
    </Txt>
  ) : null;

  return (
    <Pressable
      onPress={disabled ? undefined : () => onValueChange(!value)}
      disabled={disabled}
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          opacity: disabled ? 0.5 : 1,
          justifyContent: spread ? 'space-between' : 'flex-start',
        },
        style,
      ]}
    >
      {labelSide === 'left' && labelNode}
      <Track on={value} tone={tone} />
      {labelSide === 'right' && labelNode}
    </Pressable>
  );
};

export default Switch;
